import { buttonVariants, cn, Modal } from '@resolute/ui';
import { Award, Mail } from 'lucide-react';

import { type LeadershipMember } from '../leadership-data';

import { ProgressiveImage } from '@/components/progressive-image';
import { SocialIcon } from '@/components/social-icon';

/** Full profile detail — larger photo, full bio, experience timeline,
 *  certifications and contact/social links. */
export function LeadershipModal({
  member,
  onOpenChange,
}: {
  member: LeadershipMember | null;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Modal
      open={member !== null}
      onOpenChange={onOpenChange}
      title={member ? `${member.name} — ${member.designation}` : 'Leadership profile'}
      hideTitle
      className="max-w-2xl"
    >
      {member && (
        <div className="flex flex-col gap-6">
          <div className="flex flex-col items-center gap-4 text-center sm:flex-row sm:items-start sm:text-left">
            <ProgressiveImage
              src={member.image}
              alt={member.name}
              aspect=""
              widths={[240, 480]}
              sizes="160px"
              className="size-32 shrink-0 rounded-full shadow-md sm:size-40"
            />
            <div>
              <h2 className="font-display text-2xl font-semibold tracking-tight">{member.name}</h2>
              <p className="text-secondary mt-0.5 text-sm font-medium">
                {member.designation} · {member.department}
              </p>
              <div className="mt-3 flex flex-wrap items-center justify-center gap-3 sm:justify-start">
                <a
                  href={member.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-[#0A66C2] hover:underline"
                >
                  <SocialIcon platform="LinkedIn" className="size-4" />
                  LinkedIn
                </a>
                <a
                  href={`mailto:${member.email}`}
                  className="text-primary inline-flex items-center gap-1.5 text-sm font-medium hover:underline"
                >
                  <Mail className="size-4" aria-hidden="true" />
                  Email
                </a>
              </div>
            </div>
          </div>

          <div className="bg-muted/30 border-border/60 grid grid-cols-1 gap-3 rounded-xl border p-4 sm:grid-cols-3">
            <div>
              <p className="text-muted-foreground text-xs font-semibold uppercase tracking-wider">Degree</p>
              <p className="text-foreground mt-0.5 text-xs font-medium">{member.degree}</p>
            </div>
            <div>
              <p className="text-muted-foreground text-xs font-semibold uppercase tracking-wider">Experience</p>
              <p className="text-foreground mt-0.5 text-xs font-medium">{member.experience}</p>
            </div>
            <div>
              <p className="text-muted-foreground text-xs font-semibold uppercase tracking-wider">Area of Work</p>
              <p className="text-foreground mt-0.5 text-xs font-medium">{member.areaOfWork}</p>
            </div>
          </div>

          <p className="text-muted-foreground leading-relaxed">{member.bio}</p>

          <div>
            <h3 className="text-foreground text-sm font-semibold uppercase tracking-wide">
              Experience
            </h3>
            <ol className="border-border mt-3 flex flex-col gap-3 border-l pl-4">
              {member.timeline.map((entry) => (
                <li key={`${entry.year}-${entry.label}`}>
                  <p className="text-secondary text-xs font-semibold uppercase tracking-wide">
                    {entry.year}
                  </p>
                  <p className="text-foreground text-sm">{entry.label}</p>
                </li>
              ))}
            </ol>
          </div>

          {member.certifications.length > 0 && (
            <div>
              <h3 className="text-foreground text-sm font-semibold uppercase tracking-wide">
                Certifications
              </h3>
              <ul className="mt-3 flex flex-col gap-2">
                {member.certifications.map((cert) => (
                  <li key={cert} className="text-muted-foreground flex items-center gap-2 text-sm">
                    <Award className="text-primary size-4 shrink-0" aria-hidden="true" />
                    {cert}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <a
            href={`mailto:${member.email}`}
            className={cn(buttonVariants({ variant: 'primary' }), 'w-full sm:w-auto sm:self-start')}
          >
            Contact {member.name.split(' ')[0]}
          </a>
        </div>
      )}
    </Modal>
  );
}
